import * as React from "react";
import { useMutation, useQuery } from "react-query";
import { ApiError, SystemSettings, SystemSettingsService, AppBundle } from "gen/clients";
import { PageLoading } from "components/PageLoading/PageLoading";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useDialogState } from "hooks/useDialogState";
import { AppBundlesTable } from "./components/AppBundlesTable";
import { AppBundleFilter } from "./components/AppBundleFilter";
import { CreateAppBundlePanel } from "./components/CreateAppBundlePanel";
import { EditAppBundlePanel } from "./components/EditAppBundlePanel";
import { DisableAppBundleDialog } from "./components/DisableAppBundleDialog";
import { SnackbarAlert } from "components/SnackbarAlert/SnackbarAlert";

const AppBundlesTab: React.FC = () => {
  const [createPanelOpen, openCreatePanel, closeCreatePanel] = useDialogState();
  const [editPanelOpen, openEditPanel, closeEditPanel] = useDialogState();
  const [disableDialogOpen, openDisableDialog, closeDisableDialog] = useDialogState();
  const [successAlertOpen, showSuccessAlert] = useDialogState();
  const [selectedAppBundle, setSelectedAppBundle] = React.useState<AppBundle | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string>("");
  const [showActiveOnly, setShowActiveOnly] = React.useState<boolean>(true);

  const {
    data: systemSettings,
    isLoading,
    error,
    refetch
  } = useQuery<SystemSettings, ApiError>(["getSystemSettings"], {
    queryFn: SystemSettingsService.getSystemSettings,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const { mutate: updateSystemSettings, isLoading: isUpdating } = useMutation(
    SystemSettingsService.updateSystemSettings,
    {
      onSuccess: () => {
        refetch();
        closeDisableDialog();
        setSelectedAppBundle(null);
        setSuccessMessage("App bundle updated successfully");
        showSuccessAlert();
      }
    }
  );

  const handleEditClick = React.useCallback(
    (appBundle: AppBundle) => {
      setSelectedAppBundle(appBundle);
      openEditPanel();
    },
    [openEditPanel]
  );

  const handleDisableClick = React.useCallback(
    (appBundle: AppBundle) => {
      setSelectedAppBundle(appBundle);
      openDisableDialog();
    },
    [openDisableDialog]
  );

  const handleDisableConfirm = React.useCallback(() => {
    if (!selectedAppBundle || !systemSettings) {
      return;
    }

    const updatedAppBundles =
      systemSettings.appBundles?.map(bundle =>
        bundle.id === selectedAppBundle.id ? { ...bundle, isDisabled: !bundle.isDisabled } : bundle
      ) || [];

    updateSystemSettings({
      requestBody: {
        ...systemSettings,
        appBundles: updatedAppBundles
      }
    });
  }, [selectedAppBundle, systemSettings, updateSystemSettings]);

  const handleCreateSuccess = React.useCallback(() => {
    refetch();
    closeCreatePanel();
    setSuccessMessage("App bundle created successfully");
    showSuccessAlert();
  }, [refetch, closeCreatePanel, showSuccessAlert]);

  const handleEditSuccess = React.useCallback(() => {
    refetch();
    closeEditPanel();
    setSelectedAppBundle(null);
    setSuccessMessage("App bundle updated successfully");
    showSuccessAlert();
  }, [refetch, closeEditPanel, showSuccessAlert]);

  const allAppBundles = React.useMemo(() => {
    return systemSettings?.appBundles || [];
  }, [systemSettings?.appBundles]);

  // Filter app bundles based on switch state
  const filteredAppBundles = React.useMemo(() => {
    if (showActiveOnly) {
      return allAppBundles.filter(bundle => !bundle.isDisabled);
    }
    return allAppBundles;
  }, [allAppBundles, showActiveOnly]);

  const handleFilterChange = React.useCallback((newShowActiveOnly: boolean) => {
    setShowActiveOnly(newShowActiveOnly);
  }, []);

  if (isLoading) {
    return <PageLoading />;
  }

  if (error) {
    return <ApiErrorMessage apiError={error} />;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Card sx={{ mb: 1 }} elevation={0}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            App Bundles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage app bundles that define which applications are available to clients. Each app bundle can include
            multiple applications with specific settings and presets.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={openCreatePanel}>
          Create New App Bundle
        </Button>
        <AppBundleFilter onFilterChange={handleFilterChange} showActiveOnly={showActiveOnly} />
      </Box>

      <AppBundlesTable
        appBundles={filteredAppBundles}
        onEditClick={handleEditClick}
        onDisableClick={handleDisableClick}
        isUpdating={isUpdating}
      />

      {createPanelOpen && (
        <CreateAppBundlePanel
          onClose={closeCreatePanel}
          onSuccess={handleCreateSuccess}
          existingAppBundles={allAppBundles}
        />
      )}

      {editPanelOpen && selectedAppBundle && (
        <EditAppBundlePanel
          appBundle={selectedAppBundle}
          onClose={closeEditPanel}
          onSuccess={handleEditSuccess}
          existingAppBundles={allAppBundles}
        />
      )}

      {disableDialogOpen && selectedAppBundle && (
        <DisableAppBundleDialog
          appBundle={selectedAppBundle}
          onClose={closeDisableDialog}
          onConfirm={handleDisableConfirm}
          isLoading={isUpdating}
        />
      )}

      {successAlertOpen && (
        <SnackbarAlert message={successMessage} severity="success" horizontal="left" vertical="bottom" />
      )}
    </Box>
  );
};

export { AppBundlesTab };
